import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, AlertCircleIcon, LoaderIcon, AlertTriangle, X, Package } from 'lucide-react';
import { callWFAPI, loadRentalsToLocalStorage, logError } from '../utils/helper';
import { useEmail } from '../contexts/EmailContext';
export function RentalSummary({
  selections,
  subtotal,
  discount,
  tax,
  total,
  onCaliforniaAddressChange,
  prices,
  onRentalSubmitted,
  discountSelections,
  isGen2Mode = false
}) {
  const {
    email: globalEmail,
    setEmail: setGlobalEmail
  } = useEmail();
  const initialCustomerInfo = {
    name: '',
    phone: '',
    email: globalEmail || '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    eventName: '',
    eventVenueCityState: '',
    proposedEventDate: '',
    alternateDate: '',
    notes: '',
    preferredTechnician: ''
  };
  const [customerInfo, setCustomerInfo] = useState(initialCustomerInfo);
  const [showOwnEquipmentModal, setShowOwnEquipmentModal] = useState(false);
  const [ownEquipmentAcknowledged, setOwnEquipmentAcknowledged] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [showPreviousRentalsModal, setShowPreviousRentalsModal] = useState(false);
  const [previousRentalsCount, setPreviousRentalsCount] = useState(0);
  const [showNoHeadGearModal, setShowNoHeadGearModal] = useState(false);
  const [noHeadGearConfirmed, setNoHeadGearConfirmed] = useState(false);
  const [technicianOptions, setTechnicianOptions] = useState<Array<{
    id: string;
    name: string;
  }>>([]);
  useEffect(() => {
    // Load technicians from database
    const loadTechnicians = async () => {
      try {
        const data = {
          appname: 'tkd-score',
          reportname: 'All_Technicians'
        };
        const resp = await callWFAPI(0, 'zohoCreator', data);
        const loadedTechnicians = resp.map((item: any) => ({
          id: item.ID,
          name: item.Name
        }));
        setTechnicianOptions(loadedTechnicians);
      } catch (error) {
        console.error('Error loading technicians:', error);
        setTechnicianOptions([]);
      }
    };
    loadTechnicians();
    // Listen for storage changes (when admin updates the list)
    const handleTechnicianUpdate = () => {
      loadTechnicians();
    };
    window.addEventListener('techniciansUpdated', handleTechnicianUpdate);
    return () => {
      window.removeEventListener('techniciansUpdated', handleTechnicianUpdate);
    };
  }, []);
  useEffect(() => {
    if (globalEmail) {
      if (!customerInfo.email) {
        setCustomerInfo(prev => ({
          ...prev,
          email: globalEmail
        }));
      }
    }
  }, [globalEmail]);
  // Check email for existing rentals when email field is filled
  const checkEmailForRentals = async (email: string) => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) return;
    // Don't check if already set as global email
    if (email.toLowerCase() === globalEmail?.toLowerCase()) return;
    setIsCheckingEmail(true);
    try {
      // Set the global email
      setGlobalEmail(email.toLowerCase());
    } catch (error) {
      console.error('Error checking email for rentals:', error);
    } finally {
      setIsCheckingEmail(false);
    }
  };
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const hasEquipmentSelections = selections.gen3Body > 0 || selections.gen3Head > 0 || selections.gen3JudgesOnly > 0 || selections.fullGen3Ring > 0;
  const hasSelections = hasEquipmentSelections || selections.requireTech;
  const technicianOnlySelected = selections.requireTech && !hasEquipmentSelections;
  const hasFullRingWithoutHead = selections.fullGen3Ring > 0 && selections.gen3Head === 0;
  // Tech is required for Gen 3, optional for Gen 2
  const techRequired = !isGen2Mode;
  const hidePrices = discountSelections.usatSanctioned;
  useEffect(() => {
    const address = customerInfo.address.toLowerCase();
    const city = customerInfo.city.toLowerCase();
    const state = customerInfo.state.toLowerCase();
    const isCaliforniaAddress = state === 'california' || state === 'ca' || city.includes('california') || city.includes(' ca ') || address.includes('california') || address.includes(' ca ');
    onCaliforniaAddressChange(isCaliforniaAddress);
  }, [customerInfo.address, customerInfo.city, customerInfo.state, onCaliforniaAddressChange]);
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
    // Check for existing rentals when email is entered
    if (name === 'email' && value && /\S+@\S+\.\S+/.test(value)) {
      // Debounce the check
      const timeoutId = setTimeout(() => {
        checkEmailForRentals(value);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  const validateForm = () => {
    const newErrors = {};
    if (!customerInfo.name.trim()) newErrors.name = 'Name is required';
    if (!customerInfo.phone.trim()) newErrors.phone = 'Phone is required';
    if (!customerInfo.email.trim()) newErrors.email = 'Email is required';else if (!/\S+@\S+\.\S+/.test(customerInfo.email)) newErrors.email = 'Email is invalid';
    // Validate that at least one date is provided and is in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const proposedDate = customerInfo.proposedEventDate ? new Date(customerInfo.proposedEventDate) : null;
    const alternateDate = customerInfo.alternateDate ? new Date(customerInfo.alternateDate) : null;
    if (!proposedDate && !alternateDate) {
      newErrors.proposedEventDate = 'At least one event date is required';
      newErrors.alternateDate = 'At least one event date is required';
    } else {
      // Check if at least one date is in the future
      const proposedIsFuture = proposedDate && proposedDate >= today;
      const alternateIsFuture = alternateDate && alternateDate >= today;
      if (!proposedIsFuture && !alternateIsFuture) {
        if (proposedDate && proposedDate < today) {
          newErrors.proposedEventDate = 'Date must be in the future';
        }
        if (alternateDate && alternateDate < today) {
          newErrors.alternateDate = 'Date must be in the future';
        }
        if (!proposedDate && alternateDate && alternateDate < today) {
          newErrors.alternateDate = 'At least one future date is required';
        }
        if (!alternateDate && proposedDate && proposedDate < today) {
          newErrors.proposedEventDate = 'At least one future date is required';
        }
      }
    }
    // Tech is only required for Gen 3
    if (techRequired && !selections.requireTech) {
      newErrors.requireTech = 'On-site technician is required for Gen 3 rentals';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const saveRentalToLocalStorage = () => {
    // Removed localStorage logic - rentals are now only saved to database
  };
  const proceedWithSubmission = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      if (!globalEmail && customerInfo.email) {
        setGlobalEmail(customerInfo.email);
      }
      const discountNotes = [];
      if (discountSelections.usatSanctioned) {
        discountNotes.push('USAT Sanctioned Event Discount Requested (15%)');
      }
      if (isGen2Mode) {
        discountNotes.push('⚠️ GEN 2 EQUIPMENT RENTAL - Not eligible for USAT sanctioned events');
      }
      const notesArray = [customerInfo.notes, discountNotes.join('; ')];
      if (technicianOnlySelected) {
        notesArray.push('Customer has own Gen 3 Daedo equipment - approval required for sanctioned event');
      }
      if (hasFullRingWithoutHead && noHeadGearConfirmed) {
        notesArray.push('Customer confirmed: No e-head gear needed');
      }
      const combinedNotes = notesArray.filter(Boolean).join('\n\n');
      // Format phone number: remove all non-digits, then add +1 if no country code
      const formatPhoneNumber = (phone: string): string => {
        // Remove all non-digit characters except plus sign at the beginning
        let cleaned = phone.replace(/[^\d+]/g, '');
        // If it starts with +, keep it as is (already has country code)
        if (cleaned.startsWith('+')) {
          return cleaned;
        }
        // Remove any plus signs that aren't at the beginning
        cleaned = cleaned.replace(/\+/g, '');
        // Add +1 country code if not present
        return '+1' + cleaned;
      };
      // Ensure dates are in proper YYYY-MM-DD format
      const formatDateForDatabase = (dateString: string): string => {
        if (!dateString) return '';
        // Date input gives us YYYY-MM-DD format, ensure it's valid
        const date = new Date(dateString + 'T00:00:00');
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const zohoData = {
        Name: customerInfo.name,
        Phone_Number: formatPhoneNumber(customerInfo.phone),
        Email: customerInfo.email,
        Shipping_Address: {
          district_city: customerInfo.city || '',
          address_line_1: customerInfo.address || '',
          address_line_2: customerInfo.address2 || '',
          state_province: customerInfo.state || '',
          postal_code: customerInfo.zip || ''
        },
        Event_Name: customerInfo.eventName || '',
        Event_Venue_City_State: customerInfo.eventVenueCityState || '',
        Proposed_Event_Date: formatDateForDatabase(customerInfo.proposedEventDate),
        Alternate_Date: formatDateForDatabase(customerInfo.alternateDate),
        Notes: combinedNotes || '',
        Internal_Notes: customerInfo.notes ? `Customer Notes:\n${customerInfo.notes}` : '',
        Technician: customerInfo.preferredTechnician || '',
        Gen3_Body_Quantity: selections.gen3Body,
        Gen3_Head_Quantity: selections.gen3Head,
        Gen3_Judges_Only_Quantity: selections.gen3JudgesOnly,
        Full_Gen3_Ring_Quantity: selections.fullGen3Ring,
        Requires_Technician: selections.requireTech ? 'true' : 'false',
        Number_Of_Days: selections.numberOfDays,
        Is_Gen2_Mode: isGen2Mode ? 'true' : 'false',
        Subtotal: subtotal.toFixed(2),
        Discount_Amount: discount.toFixed(2),
        California_Tax: tax.toFixed(2),
        Total_Amount: total.toFixed(2),
        added: new Date().toISOString()
      };
      const data = {
        appname: 'tkd-score',
        formname: 'Gen_3_Scoring_Rental',
        params: JSON.stringify(zohoData)
      };
      logError('data', JSON.stringify(zohoData));
      await callWFAPI(0, 'zohoCreator', data);
      setSubmitSuccess(true);
      setCustomerInfo({
        ...initialCustomerInfo,
        email: globalEmail || ''
      });
      setSubmitted(false);
      setErrors({});
      setOwnEquipmentAcknowledged(false);
      setNoHeadGearConfirmed(false);
      if (onRentalSubmitted) {
        onRentalSubmitted();
      }
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting form to Zoho Creator:', error);
      const errorMessage = error.response?.data?.message || 'There was an error submitting your request to our database. Please try again or contact us directly.';
      logError('error', errorMessage);
      setSubmitError(errorMessage);
      if (onRentalSubmitted) {
        onRentalSubmitted();
      }
      setSubmitSuccess(true);
      setCustomerInfo({
        ...initialCustomerInfo,
        email: globalEmail || ''
      });
      setSubmitted(false);
      setErrors({});
      setOwnEquipmentAcknowledged(false);
      setNoHeadGearConfirmed(false);
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleSubmit = async () => {
    setSubmitted(true);
    if (validateForm()) {
      // Check if customer only selected technician with no equipment
      if (technicianOnlySelected && !ownEquipmentAcknowledged) {
        setShowOwnEquipmentModal(true);
        return;
      }
      // Check if customer has Full Ring but no Head gear
      if (hasFullRingWithoutHead && !noHeadGearConfirmed) {
        setShowNoHeadGearModal(true);
        return;
      }
      await proceedWithSubmission();
    }
  };
  const handleOwnEquipmentAcknowledge = async () => {
    setOwnEquipmentAcknowledged(true);
    setShowOwnEquipmentModal(false);
    await proceedWithSubmission();
  };
  const handleNoHeadGearConfirm = async () => {
    setNoHeadGearConfirmed(true);
    setShowNoHeadGearModal(false);
    await proceedWithSubmission();
  };
  const handleViewPreviousRentals = () => {
    setShowPreviousRentalsModal(false);
    // Trigger expansion of PendingRentals component
    if (onRentalSubmitted) {
      onRentalSubmitted(true); // Pass true to indicate should expand
    }
  };
  // Helper function to get the reason why submit is disabled
  const getSubmitDisabledReason = () => {
    if (!hasSelections) {
      return 'Please select equipment or services to continue';
    }
    if (techRequired && !selections.requireTech) {
      return 'On-site technician is required for Gen 3 rentals';
    }
    if (submitted && Object.keys(errors).length > 0) {
      const errorFields = Object.keys(errors);
      if (errorFields.length === 1) {
        return `Please fix the error in: ${errorFields[0]}`;
      }
      return `Please fix errors in: ${errorFields.join(', ')}`;
    }
    return null;
  };
  if (submitSuccess) {
    return <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1694">
        <div className="text-center py-8" data-id="element-1695">
          <CheckCircleIcon size={48} className="mx-auto text-green-500 mb-4" data-id="element-1696" />
          <h2 className="text-2xl font-semibold mb-2 text-green-800" data-id="element-1697">
            Request Submitted!
          </h2>
          <p className="text-slate-600" data-id="element-1698">
            Thank you for your rental request. A representative will contact you
            shortly to confirm details.
          </p>
        </div>
      </div>;
  }
  return <>
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md lg:sticky lg:top-6" data-id="element-1699">
        <h2 className="text-lg sm:text-xl font-semibold mb-4 text-blue-800" data-id="element-1700">
          Order Summary
        </h2>

        {!hasSelections ? <div className="text-slate-500 text-center py-6" data-id="element-1701">
            No items selected yet
          </div> : <div className="space-y-4" data-id="element-1702">
            {/* USAT Sanctioned Banner */}
            {hidePrices && <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4" data-id="element-1703">
                <div className="flex items-center gap-2 mb-2" data-id="element-1704">
                  <CheckCircleIcon size={18} className="text-green-600" data-id="element-1705" />
                  <span className="font-semibold text-green-800" data-id="element-1706">
                    USAT Sanctioned Event
                  </span>
                </div>
                <p className="text-sm text-green-700" data-id="element-1707">
                  Pricing for USAT sanctioned events is handled directly by our
                  team. A representative will contact you with your custom quote
                  after reviewing your request.
                </p>
              </div>}

            {/* Equipment list - show items but not prices when hidePrices */}
            {selections.gen3Body > 0 && <div className="flex justify-between" data-id="element-1708">
                <div data-id="element-1709">
                  <p className="font-medium" data-id="element-1710">
                    {isGen2Mode ? 'Gen 2' : 'Gen 3'} Body
                  </p>
                  {!hidePrices && <p className="text-sm text-slate-500" data-id="element-1711">
                      {selections.gen3Body} × $
                      {(prices.gen3Body * (isGen2Mode ? 0.85 : 1)).toFixed(2)}
                    </p>}
                  {hidePrices && <p className="text-sm text-slate-500" data-id="element-1712">
                      Qty: {selections.gen3Body}
                    </p>}
                </div>
                {!hidePrices && <p className="font-medium" data-id="element-1713">
                    $
                    {(selections.gen3Body * prices.gen3Body * (isGen2Mode ? 0.85 : 1)).toFixed(2)}
                  </p>}
              </div>}
            {selections.gen3Head > 0 && <div className="flex justify-between" data-id="element-1714">
                <div data-id="element-1715">
                  <p className="font-medium" data-id="element-1716">
                    {isGen2Mode ? 'Gen 2' : 'Gen 3'} Head
                  </p>
                  {!hidePrices && <p className="text-sm text-slate-500" data-id="element-1717">
                      {selections.gen3Head} × $
                      {(prices.gen3Head * (isGen2Mode ? 0.85 : 1)).toFixed(2)}
                    </p>}
                  {hidePrices && <p className="text-sm text-slate-500" data-id="element-1718">
                      Qty: {selections.gen3Head}
                    </p>}
                </div>
                {!hidePrices && <p className="font-medium" data-id="element-1719">
                    $
                    {(selections.gen3Head * prices.gen3Head * (isGen2Mode ? 0.85 : 1)).toFixed(2)}
                  </p>}
              </div>}
            {selections.gen3JudgesOnly > 0 && <div className="flex justify-between" data-id="element-1720">
                <div data-id="element-1721">
                  <p className="font-medium" data-id="element-1722">
                    {isGen2Mode ? 'Gen 2' : 'Gen 3'} Judges Only
                  </p>
                  {!hidePrices && <p className="text-sm text-slate-500" data-id="element-1723">
                      {selections.gen3JudgesOnly} × $
                      {(prices.gen3JudgesOnly * (isGen2Mode ? 0.85 : 1)).toFixed(2)}
                    </p>}
                  {hidePrices && <p className="text-sm text-slate-500" data-id="element-1724">
                      Qty: {selections.gen3JudgesOnly}
                    </p>}
                </div>
                {!hidePrices && <p className="font-medium" data-id="element-1725">
                    $
                    {(selections.gen3JudgesOnly * prices.gen3JudgesOnly * (isGen2Mode ? 0.85 : 1)).toFixed(2)}
                  </p>}
              </div>}
            {selections.fullGen3Ring > 0 && <div className="flex justify-between" data-id="element-1726">
                <div data-id="element-1727">
                  <p className="font-medium" data-id="element-1728">
                    Full {isGen2Mode ? 'Gen 2' : 'Gen 3'} Ring
                  </p>
                  {!hidePrices && <p className="text-sm text-slate-500" data-id="element-1729">
                      {selections.fullGen3Ring} × $
                      {(prices.fullGen3Ring * (isGen2Mode ? 0.85 : 1)).toFixed(2)}
                    </p>}
                  {hidePrices && <p className="text-sm text-slate-500" data-id="element-1730">
                      Qty: {selections.fullGen3Ring}
                    </p>}
                </div>
                {!hidePrices && <p className="font-medium" data-id="element-1731">
                    $
                    {(selections.fullGen3Ring * prices.fullGen3Ring * (isGen2Mode ? 0.85 : 1)).toFixed(2)}
                  </p>}
              </div>}

            {/* Additional Days Equipment (50% price per day) */}
            {selections.numberOfDays > 1 && !hidePrices && <>
                <div className="border-t border-blue-200 pt-3 mt-3" data-id="element-1732">
                  <p className="text-sm font-semibold text-blue-700 mb-2" data-id="element-1733">
                    Days 2-{selections.numberOfDays} Equipment (50% price per
                    day)
                  </p>
                </div>
                {selections.gen3Body > 0 && <div className="flex justify-between" data-id="element-1734">
                    <div data-id="element-1735">
                      <p className="font-medium" data-id="element-1736">
                        {isGen2Mode ? 'Gen 2' : 'Gen 3'} Body (Days 2-
                        {selections.numberOfDays})
                      </p>
                      <p className="text-sm text-slate-500" data-id="element-1737">
                        {selections.gen3Body} × $
                        {(prices.gen3Body * (isGen2Mode ? 0.85 : 1) * 0.5).toFixed(2)}{' '}
                        × {selections.numberOfDays - 1} days
                      </p>
                    </div>
                    <p className="font-medium" data-id="element-1738">
                      $
                      {(selections.gen3Body * prices.gen3Body * (isGen2Mode ? 0.85 : 1) * 0.5 * (selections.numberOfDays - 1)).toFixed(2)}
                    </p>
                  </div>}
                {selections.gen3Head > 0 && <div className="flex justify-between" data-id="element-1739">
                    <div data-id="element-1740">
                      <p className="font-medium" data-id="element-1741">
                        {isGen2Mode ? 'Gen 2' : 'Gen 3'} Head (Days 2-
                        {selections.numberOfDays})
                      </p>
                      <p className="text-sm text-slate-500" data-id="element-1742">
                        {selections.gen3Head} × $
                        {(prices.gen3Head * (isGen2Mode ? 0.85 : 1) * 0.5).toFixed(2)}{' '}
                        × {selections.numberOfDays - 1} days
                      </p>
                    </div>
                    <p className="font-medium" data-id="element-1743">
                      $
                      {(selections.gen3Head * prices.gen3Head * (isGen2Mode ? 0.85 : 1) * 0.5 * (selections.numberOfDays - 1)).toFixed(2)}
                    </p>
                  </div>}
                {selections.gen3JudgesOnly > 0 && <div className="flex justify-between" data-id="element-1744">
                    <div data-id="element-1745">
                      <p className="font-medium" data-id="element-1746">
                        {isGen2Mode ? 'Gen 2' : 'Gen 3'} Judges Only (Days 2-
                        {selections.numberOfDays})
                      </p>
                      <p className="text-sm text-slate-500" data-id="element-1747">
                        {selections.gen3JudgesOnly} × $
                        {(prices.gen3JudgesOnly * (isGen2Mode ? 0.85 : 1) * 0.5).toFixed(2)}{' '}
                        × {selections.numberOfDays - 1} days
                      </p>
                    </div>
                    <p className="font-medium" data-id="element-1748">
                      $
                      {(selections.gen3JudgesOnly * prices.gen3JudgesOnly * (isGen2Mode ? 0.85 : 1) * 0.5 * (selections.numberOfDays - 1)).toFixed(2)}
                    </p>
                  </div>}
                {selections.fullGen3Ring > 0 && <div className="flex justify-between" data-id="element-1749">
                    <div data-id="element-1750">
                      <p className="font-medium" data-id="element-1751">
                        Full {isGen2Mode ? 'Gen 2' : 'Gen 3'} Ring (Days 2-
                        {selections.numberOfDays})
                      </p>
                      <p className="text-sm text-slate-500" data-id="element-1752">
                        {selections.fullGen3Ring} × $
                        {(prices.fullGen3Ring * (isGen2Mode ? 0.85 : 1) * 0.5).toFixed(2)}{' '}
                        × {selections.numberOfDays - 1} days
                      </p>
                    </div>
                    <p className="font-medium" data-id="element-1753">
                      $
                      {(selections.fullGen3Ring * prices.fullGen3Ring * (isGen2Mode ? 0.85 : 1) * 0.5 * (selections.numberOfDays - 1)).toFixed(2)}
                    </p>
                  </div>}
              </>}

            {/* Multi-day note when prices hidden */}
            {selections.numberOfDays > 1 && hidePrices && <div className="border-t border-blue-200 pt-3 mt-3" data-id="element-1754">
                <p className="text-sm text-blue-700" data-id="element-1755">
                  {selections.numberOfDays}-day event — multi-day pricing will
                  be included in your quote.
                </p>
              </div>}

            {selections.requireTech && !hidePrices && <div className="flex justify-between py-2 bg-red-50 px-3 rounded-md border border-red-100" data-id="element-1756">
                <span className="font-medium text-red-800" data-id="element-1757">
                  On-site Technician {isGen2Mode ? '(Optional)' : '(Required)'}
                  {selections.numberOfDays > 1 ? ` - ${selections.numberOfDays} Days` : ''}
                </span>
                <span className="font-semibold text-red-800" data-id="element-1758">
                  ${(prices.technician * selections.numberOfDays).toFixed(2)}
                </span>
              </div>}

            {selections.requireTech && hidePrices && <div className="py-2 bg-red-50 px-3 rounded-md border border-red-100" data-id="element-1759">
                <span className="font-medium text-red-800" data-id="element-1760">
                  On-site Technician {isGen2Mode ? '(Optional)' : '(Required)'}
                  {selections.numberOfDays > 1 ? ` — ${selections.numberOfDays} Days` : ''}
                </span>
              </div>}

            {!selections.requireTech && hasSelections && techRequired && <div className="bg-red-50 border border-red-200 rounded-md p-3" data-id="element-1761">
                <div className="flex items-start" data-id="element-1762">
                  <AlertTriangle size={16} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1763" />
                  <p className="text-sm text-red-700" data-id="element-1764">
                    <strong data-id="element-1765">Required:</strong> You must select an on-site
                    technician to complete your Gen 3 rental request.
                  </p>
                </div>
              </div>}

            {!hidePrices && (discountSelections.collegiate || discountSelections.usatSanctioned) && <div className="border-t border-slate-200 pt-4 mt-4" data-id="element-1766">
                  <p className="font-medium text-green-700 mb-2" data-id="element-1767">
                    Discount Requests:
                  </p>
                  <div className="space-y-2" data-id="element-1768">
                    {discountSelections.usatSanctioned && <div className="bg-green-50 border-2 border-green-300 rounded-lg p-3" data-id="element-1769">
                        <div className="flex items-center justify-between" data-id="element-1770">
                          <div className="flex items-center text-green-800" data-id="element-1771">
                            <CheckCircleIcon size={16} className="mr-2 flex-shrink-0" data-id="element-1772" />
                            <span className="font-semibold" data-id="element-1773">
                              USAT Sanctioned Event
                            </span>
                          </div>
                          <span className="bg-green-600 text-white text-sm font-bold px-3 py-1 rounded-full" data-id="element-1774">
                            15% OFF
                          </span>
                        </div>
                      </div>}
                  </div>
                  <p className="text-xs text-slate-500 mt-2" data-id="element-1775">
                    This will be reviewed by a representative
                  </p>
                </div>}

            {!hidePrices && <>
                <div className="border-t border-slate-200 pt-4" data-id="element-1776">
                  <div className="flex justify-between" data-id="element-1777">
                    <p className="font-medium" data-id="element-1778">Subtotal</p>
                    <p className="font-medium" data-id="element-1779">${subtotal.toFixed(2)}</p>
                  </div>
                </div>

                {discount > 0 && <div className="flex justify-between text-green-700" data-id="element-1780">
                    <div data-id="element-1781">
                      <p className="font-semibold" data-id="element-1782">
                        USAT Sanctioned Discount (15%)
                      </p>
                    </div>
                    <p className="font-semibold" data-id="element-1783">-${discount.toFixed(2)}</p>
                  </div>}

                {tax > 0 && <div className="flex justify-between text-red-700" data-id="element-1784">
                    <div data-id="element-1785">
                      <p className="font-medium" data-id="element-1786">
                        California Use Tax ({(prices.taxRate * 100).toFixed(0)}
                        %)
                      </p>
                      <p className="text-xs" data-id="element-1787">
                        Applied to California shipping addresses
                      </p>
                    </div>
                    <p className="font-medium" data-id="element-1788">${tax.toFixed(2)}</p>
                  </div>}

                <div className="border-t border-slate-200 pt-4 mt-4" data-id="element-1789">
                  <div className="flex justify-between font-bold text-lg" data-id="element-1790">
                    <p data-id="element-1791">Total</p>
                    <p data-id="element-1792">${total.toFixed(2)}</p>
                  </div>
                </div>
              </>}

            {hidePrices && <div className="border-t border-slate-200 pt-4 mt-4" data-id="element-1793">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center" data-id="element-1794">
                  <p className="font-semibold text-blue-800 text-lg mb-1" data-id="element-1795">
                    Pricing Available Upon Review
                  </p>
                  <p className="text-sm text-blue-700" data-id="element-1796">
                    A representative will contact you with your USAT sanctioned
                    event pricing after reviewing your equipment needs.
                  </p>
                </div>
              </div>}
          </div>}

        {hasSelections && <div className="mt-6 border-t border-slate-200 pt-6" data-id="element-1797">
            <h3 className="font-medium text-lg mb-4 text-blue-800" data-id="element-1798">
              Customer Information
            </h3>
            <div className="space-y-4" data-id="element-1799">
              <div data-id="element-1800">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1801">
                  Name <span className="text-red-500" data-id="element-1802">*</span>
                </label>
                <input type="text" name="name" value={customerInfo.name} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md ${errors.name ? 'border-red-500' : 'border-slate-300'} focus:ring-blue-500 focus:border-blue-500`} placeholder="Full Name" data-id="element-1803" />
                {submitted && errors.name && <p className="text-red-500 text-xs mt-1 flex items-center" data-id="element-1804">
                    <AlertCircleIcon size={12} className="mr-1" data-id="element-1805" />
                    {errors.name}
                  </p>}
              </div>

              <div data-id="element-1806">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1807">
                  Phone <span className="text-red-500" data-id="element-1808">*</span>
                </label>
                <input type="tel" name="phone" value={customerInfo.phone} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md ${errors.phone ? 'border-red-500' : 'border-slate-300'} focus:ring-blue-500 focus:border-blue-500`} placeholder="Phone Number" data-id="element-1809" />
                {submitted && errors.phone && <p className="text-red-500 text-xs mt-1 flex items-center" data-id="element-1810">
                    <AlertCircleIcon size={12} className="mr-1" data-id="element-1811" />
                    {errors.phone}
                  </p>}
              </div>

              <div data-id="element-1812">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1813">
                  Email <span className="text-red-500" data-id="element-1814">*</span>
                </label>
                <div className="relative" data-id="element-1815">
                  <input type="email" name="email" value={customerInfo.email} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md ${errors.email ? 'border-red-500' : 'border-slate-300'} focus:ring-blue-500 focus:border-blue-500`} placeholder="Email Address" data-id="element-1816" />
                  {isCheckingEmail && <div className="absolute right-3 top-1/2 -translate-y-1/2" data-id="element-1817">
                      <LoaderIcon size={16} className="animate-spin text-blue-600" data-id="element-1818" />
                    </div>}
                </div>
                {submitted && errors.email && <p className="text-red-500 text-xs mt-1 flex items-center" data-id="element-1819">
                    <AlertCircleIcon size={12} className="mr-1" data-id="element-1820" />
                    {errors.email}
                  </p>}
              </div>

              <div data-id="element-1821">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1822">
                  Address <span className="text-red-500" data-id="element-1823">*</span>
                </label>
                <input type="text" name="address" value={customerInfo.address} onChange={handleInputChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Street Address" data-id="element-1824" />
              </div>

              <div data-id="element-1825">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1826">
                  Address 2
                </label>
                <input type="text" name="address2" value={customerInfo.address2} onChange={handleInputChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Apt, Suite, Unit, etc. (optional)" data-id="element-1827" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1828">
                <div data-id="element-1829">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1830">
                    City <span className="text-red-500" data-id="element-1831">*</span>
                  </label>
                  <input type="text" name="city" value={customerInfo.city} onChange={handleInputChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="City" data-id="element-1832" />
                </div>

                <div data-id="element-1833">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1834">
                    State <span className="text-red-500" data-id="element-1835">*</span>
                  </label>
                  <select name="state" value={customerInfo.state} onChange={handleInputChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base" data-id="element-1836">
                    <option value="" data-id="element-1837">Select State</option>
                    <option value="AL" data-id="element-1838">Alabama</option>
                    <option value="AK" data-id="element-1839">Alaska</option>
                    <option value="AZ" data-id="element-1840">Arizona</option>
                    <option value="AR" data-id="element-1841">Arkansas</option>
                    <option value="CA" data-id="element-1842">California</option>
                    <option value="CO" data-id="element-1843">Colorado</option>
                    <option value="CT" data-id="element-1844">Connecticut</option>
                    <option value="DE" data-id="element-1845">Delaware</option>
                    <option value="FL" data-id="element-1846">Florida</option>
                    <option value="GA" data-id="element-1847">Georgia</option>
                    <option value="HI" data-id="element-1848">Hawaii</option>
                    <option value="ID" data-id="element-1849">Idaho</option>
                    <option value="IL" data-id="element-1850">Illinois</option>
                    <option value="IN" data-id="element-1851">Indiana</option>
                    <option value="IA" data-id="element-1852">Iowa</option>
                    <option value="KS" data-id="element-1853">Kansas</option>
                    <option value="KY" data-id="element-1854">Kentucky</option>
                    <option value="LA" data-id="element-1855">Louisiana</option>
                    <option value="ME" data-id="element-1856">Maine</option>
                    <option value="MD" data-id="element-1857">Maryland</option>
                    <option value="MA" data-id="element-1858">Massachusetts</option>
                    <option value="MI" data-id="element-1859">Michigan</option>
                    <option value="MN" data-id="element-1860">Minnesota</option>
                    <option value="MS" data-id="element-1861">Mississippi</option>
                    <option value="MO" data-id="element-1862">Missouri</option>
                    <option value="MT" data-id="element-1863">Montana</option>
                    <option value="NE" data-id="element-1864">Nebraska</option>
                    <option value="NV" data-id="element-1865">Nevada</option>
                    <option value="NH" data-id="element-1866">New Hampshire</option>
                    <option value="NJ" data-id="element-1867">New Jersey</option>
                    <option value="NM" data-id="element-1868">New Mexico</option>
                    <option value="NY" data-id="element-1869">New York</option>
                    <option value="NC" data-id="element-1870">North Carolina</option>
                    <option value="ND" data-id="element-1871">North Dakota</option>
                    <option value="OH" data-id="element-1872">Ohio</option>
                    <option value="OK" data-id="element-1873">Oklahoma</option>
                    <option value="OR" data-id="element-1874">Oregon</option>
                    <option value="PA" data-id="element-1875">Pennsylvania</option>
                    <option value="RI" data-id="element-1876">Rhode Island</option>
                    <option value="SC" data-id="element-1877">South Carolina</option>
                    <option value="SD" data-id="element-1878">South Dakota</option>
                    <option value="TN" data-id="element-1879">Tennessee</option>
                    <option value="TX" data-id="element-1880">Texas</option>
                    <option value="UT" data-id="element-1881">Utah</option>
                    <option value="VT" data-id="element-1882">Vermont</option>
                    <option value="VA" data-id="element-1883">Virginia</option>
                    <option value="WA" data-id="element-1884">Washington</option>
                    <option value="WV" data-id="element-1885">West Virginia</option>
                    <option value="WI" data-id="element-1886">Wisconsin</option>
                    <option value="WY" data-id="element-1887">Wyoming</option>
                  </select>
                </div>
              </div>

              <div data-id="element-1888">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1889">
                  ZIP Code <span className="text-red-500" data-id="element-1890">*</span>
                </label>
                <input type="text" name="zip" value={customerInfo.zip} onChange={handleInputChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="ZIP Code" maxLength={10} data-id="element-1891" />
                {tax > 0 && !hidePrices && <p className="text-red-600 text-xs mt-1" data-id="element-1892">
                    California shipping address:{' '}
                    {(prices.taxRate * 100).toFixed(0)}% use tax will be applied
                  </p>}
              </div>

              <div data-id="element-1893">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1894">
                  Event Name
                </label>
                <input type="text" name="eventName" value={customerInfo.eventName} onChange={handleInputChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Name of Event" data-id="element-1895" />
              </div>

              <div data-id="element-1896">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1897">
                  Event Venue City/State
                </label>
                <input type="text" name="eventVenueCityState" value={customerInfo.eventVenueCityState} onChange={handleInputChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Los Angeles, CA" data-id="element-1898" />
              </div>

              <div data-id="element-1899">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1900">
                  Proposed Event Date <span className="text-red-500" data-id="element-1901">*</span>
                </label>
                <input type="date" name="proposedEventDate" value={customerInfo.proposedEventDate} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md ${errors.proposedEventDate ? 'border-red-500' : 'border-slate-300'} focus:ring-blue-500 focus:border-blue-500`} data-id="element-1902" />
                {submitted && errors.proposedEventDate && <p className="text-red-500 text-xs mt-1 flex items-center" data-id="element-1903">
                    <AlertCircleIcon size={12} className="mr-1" data-id="element-1904" />
                    {errors.proposedEventDate}
                  </p>}
              </div>

              <div data-id="element-1905">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1906">
                  Alternate Date{' '}
                  {!customerInfo.proposedEventDate && <span className="text-red-500" data-id="element-1907">*</span>}
                </label>
                <input type="date" name="alternateDate" value={customerInfo.alternateDate} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md ${errors.alternateDate ? 'border-red-500' : 'border-slate-300'} focus:ring-blue-500 focus:border-blue-500`} data-id="element-1908" />
                {submitted && errors.alternateDate && <p className="text-red-500 text-xs mt-1 flex items-center" data-id="element-1909">
                    <AlertCircleIcon size={12} className="mr-1" data-id="element-1910" />
                    {errors.alternateDate}
                  </p>}
                {!customerInfo.proposedEventDate && !customerInfo.alternateDate && <p className="text-slate-500 text-xs mt-1" data-id="element-1911">
                      At least one future date is required
                    </p>}
              </div>

              <div data-id="element-1912">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1913">
                  Preferred Technician
                </label>
                <select name="preferredTechnician" value={customerInfo.preferredTechnician} onChange={handleInputChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-1914">
                  <option value="" data-id="element-1915">Select a technician...</option>
                  {technicianOptions.map(tech => <option key={tech.id} value={tech.id} data-id="element-1916">
                      {tech.name}
                    </option>)}
                </select>
              </div>

              <div data-id="element-1917">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1918">
                  Notes
                </label>
                <textarea name="notes" value={customerInfo.notes} onChange={handleInputChange} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Additional information or special requests" data-id="element-1919" />
              </div>
            </div>
          </div>}

        <div className="mt-6" data-id="element-1920">
          <button type="button" disabled={!hasSelections || isSubmitting || techRequired && !selections.requireTech} onClick={handleSubmit} className={`w-full py-3 px-4 rounded-md flex justify-center items-center ${hasSelections && !isSubmitting && (!techRequired || selections.requireTech) ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`} data-id="element-1921">
            {isSubmitting ? <>
                <LoaderIcon size={20} className="mr-2 animate-spin" data-id="element-1922" />
                Submitting...
              </> : <>
                <CheckCircleIcon size={20} className="mr-2" data-id="element-1923" />
                Complete Rental Request
              </>}
          </button>

          {/* Show specific error message when button is disabled */}
          {(!hasSelections || techRequired && !selections.requireTech || submitted && Object.keys(errors).length > 0) && !isSubmitting && <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-1924">
                <div className="flex items-start" data-id="element-1925">
                  <AlertCircleIcon size={16} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1926" />
                  <p className="text-sm text-red-700 font-medium" data-id="element-1927">
                    {getSubmitDisabledReason()}
                  </p>
                </div>
              </div>}
        </div>

        {submitError && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm" data-id="element-1928">
            {submitError}
          </div>}

        <div className="mt-4 text-xs text-slate-500 text-center" data-id="element-1929">
          A representative will contact you to confirm your order and arrange
          payment.
        </div>
      </div>

      {/* No Head Gear Confirmation Modal */}
      {showNoHeadGearModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1930">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full" data-id="element-1931">
            <div className="p-6" data-id="element-1932">
              <div className="flex items-start mb-4" data-id="element-1933">
                <AlertTriangle className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1934" />
                <div data-id="element-1935">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2" data-id="element-1936">
                    No E-Head Gear Selected
                  </h3>
                  <p className="text-slate-600 text-sm" data-id="element-1937">
                    You've selected Full Gen 3 Ring equipment but haven't added
                    any Gen 3 Head gear.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4" data-id="element-1938">
                <p className="text-sm text-blue-800 mb-2" data-id="element-1939">
                  <strong data-id="element-1940">Please confirm:</strong>
                </p>
                <p className="text-xs text-blue-800" data-id="element-1941">
                  You do not need e-head gear for this event. The Full Gen 3
                  Ring includes body sensors and judges only (head gear not
                  included).
                </p>
              </div>

              <div className="flex gap-3" data-id="element-1942">
                <button onClick={() => {
              setShowNoHeadGearModal(false);
              setSubmitted(false);
            }} className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1943">
                  Go Back
                </button>
                <button onClick={handleNoHeadGearConfirm} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-id="element-1944">
                  Confirm - No Head Gear
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Previous Rentals Found Modal */}
      {showPreviousRentalsModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1945">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full" data-id="element-1946">
            <div className="p-6" data-id="element-1947">
              <div className="flex items-start mb-4" data-id="element-1948">
                <Package className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1949" />
                <div data-id="element-1950">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2" data-id="element-1951">
                    Previous Rental Requests Found
                  </h3>
                  <p className="text-slate-600 text-sm" data-id="element-1952">
                    You have {previousRentalsCount} previous rental request
                    {previousRentalsCount !== 1 ? 's' : ''} associated with this
                    email address.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4" data-id="element-1953">
                <p className="text-xs text-blue-800" data-id="element-1954">
                  Your previous rental requests will be displayed above. You can
                  view their status and details at any time.
                </p>
              </div>

              <div className="flex gap-3" data-id="element-1955">
                <button onClick={() => setShowPreviousRentalsModal(false)} className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1956">
                  Close
                </button>
                <button onClick={handleViewPreviousRentals} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-id="element-1957">
                  View Requests
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Own Equipment Acknowledgment Modal */}
      {showOwnEquipmentModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1958">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full" data-id="element-1959">
            <div className="p-6" data-id="element-1960">
              <div className="flex items-start mb-4" data-id="element-1961">
                <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0 mt-1" data-id="element-1962" />
                <div data-id="element-1963">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2" data-id="element-1964">
                    Own Equipment Acknowledgment
                  </h3>
                  <p className="text-slate-600 text-sm" data-id="element-1965">
                    I have my own generation 3 Daedo Equipment and require
                    approval to use it at a sanctioned event.
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4" data-id="element-1966">
                <p className="text-xs text-yellow-800" data-id="element-1967">
                  By acknowledging this, you confirm that you own Gen 3 Daedo
                  equipment and understand that approval is required to use it
                  at sanctioned events. A representative will contact you to
                  verify and approve your equipment.
                </p>
              </div>

              <div className="flex gap-3" data-id="element-1968">
                <button onClick={() => {
              setShowOwnEquipmentModal(false);
              setSubmitted(false);
            }} className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1969">
                  Cancel
                </button>
                <button onClick={handleOwnEquipmentAcknowledge} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-id="element-1970">
                  I Acknowledge
                </button>
              </div>
            </div>
          </div>
        </div>}
    </>;
}